import React, { useState, useEffect, useRef } from 'react';
import { X, FileText } from 'lucide-react';

const CheckoutNotesModal = ({ 
  appointment, 
  onClose, 
  onComplete, 
  db 
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const modalRef = useRef();

  const handleCompleteWithNotes = async () => {
    // Validation : max 500 caractères
    if (notes.length > 500) {
      setError(`Les notes ne peuvent pas dépasser 500 caractères (actuellement: ${notes.length})`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onComplete(notes);
      onClose();
    } catch (error) {
      console.error("Erreur lors de la clôture avec notes:", error);
      setError("Erreur lors de la sauvegarde des notes. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };


  const characterCount = notes.length;
  const isOverLimit = characterCount > 500;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[100]">
      <div 
        ref={modalRef}
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()} // Empêche la fermeture quand on clique dans le modal
      >
        <button 
          onClick={onClose} 
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          disabled={isSubmitting}
        >
          <X size={24} />
        </button>
        
        <div className="flex items-center mb-4">
          <FileText className="mr-2 text-blue-500" size={24} />
          <h2 className="text-xl font-bold">Notes de clôture</h2>
        </div>

        <div className="mb-4">
          <p className="text-gray-600 mb-2">
            Ajoutez des notes optionnelles sur ce qui s'est passé pendant l'intervention (max 500 caractères) :
          </p>
          
          <div className="relative">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Décrivez ce qui s'est passé pendant l'intervention..."
              className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={500}
              disabled={isSubmitting}
              onClick={(e) => e.stopPropagation()} // Empêche la fermeture quand on clique dans le textarea
            />
            
            <div className={`text-xs mt-1 text-right ${isOverLimit ? 'text-red-500' : 'text-gray-500'}`}>
              {characterCount}/500 caractères
            </div>
          </div>

          {error && (
            <div className="mt-2 text-red-500 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          
          <button
            onClick={handleCompleteWithNotes}
            disabled={isSubmitting || isOverLimit}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'En cours...' : 'Valider avec notes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutNotesModal;
